import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import {
    Modal, ModalHeader, ModalBody, Form, Row, Col,
    FormGroup, Label, Input, Button, ModalFooter, Spinner
} from 'reactstrap';
import Select from 'react-select';

import { AgendaContext } from '../../context/AgendaContext';
import { TipoAgendaContext } from '../../context/TipoAgendaContext';
import { TerceiroContext } from '../../context/TerceiroContext';
import { EditAgendaType } from '../../context/hooks/useAgenda';
import { AuthContext } from '../../context/AuthContext';
import { CursoContext } from '../../context/CursoContext';
import { CurrencyInput } from '../Form/CurrencyInput';
import ModalConfirm from '../ModalConfirm/ModalConfirm';

interface Props {
    isOpen: boolean;
    toggle: () => void;
    agenda: EditAgendaType | null;
    onUpdated?: () => void;
    onDeleted?: () => void;
}

type RemoveAction = 'remove' | 'reallocate';
type ServicoItem = { servicoId: number; servicoName: string; value?: number };

const semServicoOpt = { value: 0, label: '——' };

export const EditAgendaGerenteModal: React.FC<Props> = ({ isOpen, toggle, agenda, onUpdated, onDeleted }) => {
    const {
        updateAgenda,
        deleteAgenda,
        listAgenda,
        realocarFotografo,
        checkConflitoHorarioFotografo,
        addCursoNaAgenda,
        removeCursoDaAgenda,
    } = useContext(AgendaContext);
    const { listTipoAgenda, getAllTipoAgendaPaged } = useContext(TipoAgendaContext);
    const {
        listTerceiro,
        getTerceiroPaged,
        listTerceiroByAgenda,
        handleGetTerceiroByAgendaId,
        handleGetServicosByTerceiro,
    } = useContext(TerceiroContext);
    const { objUserTk } = useContext(AuthContext);
    const { handleGetCursoByEmpresaId, listCurso } = useContext(CursoContext);

    const [form, setForm] = useState<EditAgendaType | null>(agenda);

    type CursoBadge = { id: number; name: string };
    const [linkedCursos, setLinkedCursos] = useState<CursoBadge[]>([]);
    const [addingCursoBusy, setAddingCursoBusy] = useState(false);
    const [removingCursoBusy, setRemovingCursoBusy] = useState<number | null>(null);

    const [selectedFotografos, setSelectedFotografos] = useState<number[]>([]);
    const [servicosMap, setServicosMap] = useState<Record<number, ServicoItem[]>>({});
    const [loadingServicos, setLoadingServicos] = useState<Record<number, boolean>>({});
    const [chosenServico, setChosenServico] = useState<Record<number, number | null>>({});
    const [chosenValor, setChosenValor] = useState<Record<number, number | null>>({});

    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const [removeTargetPhotographerId, setRemoveTargetPhotographerId] = useState<number | null>(null);
    const [removeAction, setRemoveAction] = useState<RemoveAction>('remove');
    const [reallocateAgendaId, setReallocateAgendaId] = useState<number | null>(null);

    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (isOpen && agenda) {
            setForm(agenda);
            setSelectedFotografos([]);
            setServicosMap({});
            setChosenServico({});
            setChosenValor({});
            setLoadingServicos({});

            setLinkedCursos([]);
            handleGetCursoByEmpresaId(objUserTk.EmpresaId);

            handleGetTerceiroByAgendaId(agenda.id);
            handleGetCursoByEmpresaId(objUserTk.EmpresaId);
            getAllTipoAgendaPaged(1, 1000, objUserTk.EmpresaId);
            getTerceiroPaged(1, '', objUserTk.EmpresaId, 1000, 'Fotógrafo');
        }
    }, [isOpen, agenda]);

    useEffect(() => {
        if (!isOpen || !agenda) return;

        const ids: number[] =
            (agenda as any)?.cursoIds?.length ? (agenda as any).cursoIds
                : ((agenda as any)?.cursoId != null ? [(agenda as any).cursoId] : []);
        const namesFromDto: string[] =
            (agenda as any)?.cursoNames?.length ? (agenda as any).cursoNames
                : ((agenda as any)?.cursoName ? [(agenda as any).cursoName] : []);

        const resolved: CursoBadge[] = (ids as number[]).map((id: number, idx: number) => {
            const fromList = listCurso.find(c => c.id === id)?.name;
            const fromDto = namesFromDto[idx] ?? namesFromDto.find(Boolean);
            return { id, name: fromList || fromDto || `Curso #${id}` };
        });

        setLinkedCursos(resolved);
    }, [isOpen, agenda, listCurso]);

    useEffect(() => {
        if (!isOpen) return;

        const bootstrap = async () => {
            const ids = (listTerceiroByAgenda || []).map(t => t.id);
            setSelectedFotografos(ids);

            ids.forEach(id => setLoadingServicos(prev => ({ ...prev, [id]: true })));
            const results = await Promise.all(ids.map(async (terceiroId) => {
                const list = await handleGetServicosByTerceiro(terceiroId);
                const options: ServicoItem[] = (list || []).map((x: any) => ({
                    servicoId: x?.servicoId ?? x?.tipoServicoId ?? x?.id,
                    servicoName: x?.servicoName ?? x?.name ?? x?.tipoServicoName ?? `Serviço #${x?.servicoId ?? x?.id}`,
                    value: x?.value,
                })).filter(o => o.servicoId != null);

                return { terceiroId, options };
            }));

            if (!mountedRef.current) return;

            setServicosMap(prev => {
                const copy = { ...prev };
                results.forEach(({ terceiroId, options }) => { copy[terceiroId] = options; });
                return copy;
            });

            setChosenServico(prev => {
                const baseServico: Record<number, number | null> = { ...prev };
                const baseValor: Record<number, number | null> = {};

                (listTerceiroByAgenda || []).forEach((t: any) => {
                    const tid = t.id;
                    const options = results.find(r => r.terceiroId === tid)?.options ?? [];

                    const sid =
                        t?.servicoId ??
                        t?.ServicoId ??
                        t?.tipoServicoId ??
                        t?.TipoServicoId ??
                        t?.tipoServico?.id ??
                        t?.TipoServico?.Id ??
                        options[0]?.servicoId ??
                        null;

                    baseServico[tid] = sid;

                    const valorDto =
                        t?.valorServico ??
                        t?.ValorServico ??
                        null;

                    const defaultFromService = sid != null
                        ? (options.find(o => o.servicoId === sid)?.value ?? null)
                        : null;

                    baseValor[tid] = (typeof valorDto === 'number') ? valorDto : defaultFromService;
                });

                setChosenValor(baseValor);
                return baseServico;
            });

            results.forEach(({ terceiroId }) =>
                setLoadingServicos(prev => ({ ...prev, [terceiroId]: false }))
            );
        };

        bootstrap();
    }, [isOpen, listTerceiroByAgenda]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (!form) return;
        const { name, value } = e.target;
        setForm({ ...form, [name]: name === 'step' || name === 'tipoAgendaId' ? +value : value });
    };

    const listCursoOptions = listCurso.map(curso => ({ value: curso.id, label: curso.name }));
    const linkedIds = new Set(linkedCursos.map(c => c.id));
    const addableCursoOptions = listCursoOptions.filter(o => !linkedIds.has(o.value));

    const handleAddCurso = async (opt: { value: number; label: string } | null) => {
        if (!form || !opt) return;
        if (linkedIds.has(opt.value)) return;

        setAddingCursoBusy(true);
        try {
            const ok = await addCursoNaAgenda(form.id, opt.value);
            if (ok) {
                setLinkedCursos(prev => [...prev, { id: opt.value, name: opt.label }]);
            }
        } finally {
            setAddingCursoBusy(false);
        }
    };

    const handleRemoveCurso = async (cursoId: number) => {
        if (!form) return;
        setRemovingCursoBusy(cursoId);
        try {
            const ok = await removeCursoDaAgenda(form.id, cursoId);
            if (ok) {
                setLinkedCursos(prev => prev.filter(c => c.id !== cursoId));
            }
        } finally {
            setRemovingCursoBusy(null);
        }
    };

    const addFotografo = async (opt: { value: number; label: string } | null) => {
        if (!opt || !form) return;
        const id = opt.value;
        if (selectedFotografos.includes(id)) return;

        try {
            const conflito = await (checkConflitoHorarioFotografo?.(id, form.startDate, form.endDate) as Promise<boolean>);
            if (conflito) {
                return;
            }
        } catch {
            return;
        }

        setSelectedFotografos(prev => [...prev, id]);

        setLoadingServicos(prev => ({ ...prev, [id]: true }));
        const list = await handleGetServicosByTerceiro(id);
        const options: ServicoItem[] = (list || []).map((x: any) => ({
            servicoId: x?.servicoId ?? x?.tipoServicoId ?? x?.id,
            servicoName: x?.servicoName ?? x?.name ?? x?.tipoServicoName ?? `Serviço #${x?.servicoId ?? x?.id}`,
            value: x?.value,
        })).filter(o => o.servicoId != null);

        if (!mountedRef.current) return;

        const sid = options[0]?.servicoId ?? null;
        const val = sid != null ? (options.find(o => o.servicoId === sid)?.value ?? null) : null;

        setServicosMap(prev => ({ ...prev, [id]: options }));
        setChosenServico(prev => ({ ...prev, [id]: options[0]?.servicoId ?? null }));
        setChosenValor(prev => ({ ...prev, [id]: val }));
        setLoadingServicos(prev => ({ ...prev, [id]: false }));
    };

    const refreshServicos = async (terceiroId: number) => {
        setLoadingServicos(prev => ({ ...prev, [terceiroId]: true }));
        const list = await handleGetServicosByTerceiro(terceiroId);
        const options: ServicoItem[] = (list || []).map((x: any) => ({
            servicoId: x?.servicoId ?? x?.tipoServicoId ?? x?.id,
            servicoName: x?.servicoName ?? x?.name ?? x?.tipoServicoName ?? `Serviço #${x?.servicoId ?? x?.id}`,
            value: x?.value,
        })).filter(o => o.servicoId != null);

        if (!mountedRef.current) return;
        setServicosMap(prev => ({ ...prev, [terceiroId]: options }));
        setChosenServico(prev => {
            const current = prev[terceiroId] ?? null;
            const exists = options.some(o => o.servicoId === current);
            return { ...prev, [terceiroId]: exists ? current : (options[0]?.servicoId ?? null) };
        });
        setLoadingServicos(prev => ({ ...prev, [terceiroId]: false }));
    };

    const setServicoFor = (terceiroId: number, servicoId: number | null) => {
        setChosenServico(prev => ({ ...prev, [terceiroId]: servicoId }));

        const options = servicosMap[terceiroId] || [];
        const defaultVal = servicoId != null
            ? (options.find(o => o.servicoId === servicoId)?.value ?? null)
            : null;

        setChosenValor(prev => ({ ...prev, [terceiroId]: defaultVal }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form) return;

        const payload: any = {
            ...form,
            terceiros: selectedFotografos.map(id => ({
                terceiroId: id,
                servicoId: chosenServico[id] ?? null,
                valorServico: chosenValor[id] ?? null,
            })),
        };

        const ok = await updateAgenda(payload);
        if (ok) {
            onUpdated?.();
            toggle();
        }
    };

    const [confirmDeleteEventoOpen, setConfirmDeleteEventoOpen] = useState(false);

    const handleDelete = () => {
        if (!form) return;
        setConfirmDeleteEventoOpen(true);
    };

    const executeDeleteEvento = async () => {
        if (!form) return;
        setConfirmDeleteEventoOpen(false);
        const ok = await deleteAgenda(form.id);
        if (ok) {
            onDeleted?.();
            toggle();
        }
    };

    const openRemoveModal = (photographerId: number) => {
        setRemoveTargetPhotographerId(photographerId);
        setRemoveAction('remove');
        setReallocateAgendaId(null);
        setRemoveModalOpen(true);
    };

    const confirmRemoveOrReallocate = async () => {
        if (!form || removeTargetPhotographerId == null) return;

        if (removeAction === 'remove') {
            setSelectedFotografos(prev => prev.filter(id => id !== removeTargetPhotographerId));
            setChosenServico(prev => {
                const copy = { ...prev }; delete copy[removeTargetPhotographerId]; return copy;
            });
            setChosenValor(prev => {
                const c = { ...prev }; delete c[removeTargetPhotographerId]; return c;
            });
            setServicosMap(prev => {
                const copy = { ...prev }; delete copy[removeTargetPhotographerId]; return copy;
            });
            setRemoveModalOpen(false);
            return;
        }

        if (!reallocateAgendaId) {
            alert('Selecione um evento para realocar.');
            return;
        }

        const ok = await realocarFotografo({
            fromAgendaId: form.id,
            toAgendaId: reallocateAgendaId,
            terceiroId: removeTargetPhotographerId,
        });

        if (ok) {
            setSelectedFotografos(prev => prev.filter(id => id !== removeTargetPhotographerId));
            setChosenServico(prev => {
                const copy = { ...prev }; delete copy[removeTargetPhotographerId]; return copy;
            });
            setServicosMap(prev => {
                const copy = { ...prev }; delete copy[removeTargetPhotographerId]; return copy;
            });
            onUpdated?.();
            setRemoveModalOpen(false);
        }
    };

    if (!form) return null;

    // const listCursoOptions = listCurso.map(curso => ({ value: curso.id, label: curso.name }));
    const listFotografoOptions = listTerceiro.map(t => ({ value: t.id, label: `${t.id} - ${t.name}` }));
    const reallocateAgendaOptions = listAgenda.filter(a => a.id !== form.id).map(a => ({ value: a.id, label: `${a.id} - ${a.description}` }));

    const getTerceiroName = (id: number) =>
        listTerceiro.find(t => t.id === id)?.name ||
        listTerceiroByAgenda.find(t => t.id === id)?.name ||
        `#${id}`;

    const getConfirmBadgeClass = (id: number) => {
        const tAgenda = listTerceiroByAgenda.find(t => t.id === id);
        if (tAgenda?.confirm === true) return 'badge-success';
        if (tAgenda?.confirm === false) return 'badge-danger';
        return 'badge-primary';
    };

    const getSelectedServiceOption = (id: number) => {
        const sid = chosenServico[id];
        const opts = servicosMap[id] || [];
        if (sid == null) return semServicoOpt;
        const found = opts.find(o => o.servicoId === sid);
        return found ? { value: found.servicoId, label: found.servicoName } : semServicoOpt;
    };

    const getServiceOptions = (id: number) => {
        const opts = servicosMap[id] || [];
        return [semServicoOpt, ...opts.map(o => ({ value: o.servicoId, label: o.servicoName }))];
    };

    const addPhotographerIsLoading = isOpen && listTerceiro.length === 0;

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="xl">
                <ModalHeader toggle={toggle}>Editar evento (gerente)</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label">Tipo</Label>
                                    <Input className="form-control-alternative" type="select" name="tipoAgendaId" value={form.tipoAgendaId} onChange={onChange} disabled>
                                        {listTipoAgenda.map(t => (
                                            <option key={t.id} value={t.id}>{t.description}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label">Descrição</Label>
                                    <Input className="form-control-alternative" name="description" value={form.description} onChange={onChange} disabled />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup className="form-control-label">
                                    <Label>Etapa</Label>
                                    <Input className="form-control-alternative" type="select" name="step" value={form.step} onChange={onChange} disabled>
                                        <option value={1}>Aberto</option>
                                        <option value={2}>Em andamento</option>
                                        <option value={3}>Concluído</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <FormGroup className="form-control-label">
                                    <Label>Local</Label>
                                    <Input className="form-control-alternative" name="location" value={form.location || ''} onChange={onChange} disabled />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup className="form-control-label">
                                    <Label>Responsável</Label>
                                    <Input className="form-control-alternative" name="responsible" value={form.responsible || ''} onChange={onChange} disabled />
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* <Row>
                            <Col md="12">
                                <FormGroup>
                                    <Label className="form-control-label">Curso</Label>
                                    <Select
                                        className="form-control-alternative"
                                        options={listCursoOptions}
                                        value={listCursoOptions.find(opt => opt.value === form.cursoId) || null}
                                        onChange={(selectedOption) => setForm(prev => prev ? { ...prev, cursoId: selectedOption?.value || null } : prev)}
                                        isClearable
                                        placeholder="Selecione um curso..."
                                        isDisabled
                                    />
                                </FormGroup>
                            </Col>
                        </Row> */}

                        <Row>
                            <Col md="12">
                                <FormGroup>
                                    <Label className="form-control-label d-block">Cursos</Label>

                                    <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                                        <div style={{ minWidth: 280, flex: 1 }}>
                                            <Select
                                                classNamePrefix="select"
                                                options={addableCursoOptions}
                                                onChange={(opt: any) => handleAddCurso(opt)}
                                                placeholder="Adicionar curso…"
                                                isClearable
                                                isSearchable
                                                isDisabled={addingCursoBusy}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menu: (p) => ({ ...p, zIndex: 9999 }),
                                                    menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                                                }}
                                            />
                                        </div>
                                        {addingCursoBusy && <Spinner size="sm" />}
                                    </div>

                                    <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                                        {linkedCursos.length === 0 && (
                                            <span className="text-muted">Nenhum curso vinculado</span>
                                        )}
                                        {linkedCursos.map(curso => (
                                            <span key={curso.id} className="badge badge-primary mr-2" style={{ fontSize: 13 }}>
                                                {curso.name}
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger ml-1"
                                                    onClick={() => handleRemoveCurso(curso.id)}
                                                    disabled={removingCursoBusy === curso.id}
                                                >
                                                    x
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label">Início</Label>
                                    <Input className="form-control-alternative" type="datetime-local" name="startDate" value={form.startDate.slice(0, 16)} onChange={onChange} disabled />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label">Fim</Label>
                                    <Input className="form-control-alternative" type="datetime-local" name="endDate" value={form.endDate.slice(0, 16)} onChange={onChange} disabled />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label">Beca</Label>
                                    <Input className="form-control-alternative" type="text" name="beca" value={form.beca} onChange={onChange} disabled />
                                </FormGroup>
                            </Col>

                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label d-block">Fotógrafos & serviços</Label>

                                    <div className="d-flex align-items-center mb-3">
                                        <Select
                                            classNamePrefix="select"
                                            className="w-100"
                                            options={listFotografoOptions.filter(opt => !selectedFotografos.includes(opt.value))}
                                            onChange={(opt: any) => addFotografo(opt)}
                                            placeholder="Adicionar fotógrafo…"
                                            isClearable
                                            isSearchable
                                            isLoading={addPhotographerIsLoading}
                                            noOptionsMessage={() => addPhotographerIsLoading ? 'Carregando…' : 'Sem resultados'}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                        />
                                    </div>

                                    {selectedFotografos.length > 0 ? (
                                        <div>
                                            {selectedFotografos.map(id => {
                                                const badgeColor = getConfirmBadgeClass(id);
                                                const serviceOptions = getServiceOptions(id);
                                                const selectedService = getSelectedServiceOption(id);
                                                const isLoading = !!loadingServicos[id];

                                                return (
                                                    <div key={id} className="d-flex align-items-center py-2" style={{ gap: 8 }}>
                                                        <span className={`badge ${badgeColor}`} title="Status de confirmação">
                                                            {id} - {getTerceiroName(id)}
                                                        </span>

                                                        <div style={{ minWidth: 260, flex: 1 }}>
                                                            <Select
                                                                classNamePrefix="select"
                                                                options={serviceOptions}
                                                                value={selectedService}
                                                                onChange={(opt: any) => setServicoFor(id, opt?.value ? Number(opt.value) : null)}
                                                                isLoading={isLoading}
                                                                isClearable={false}
                                                                isSearchable
                                                                placeholder={isLoading ? 'Carregando serviços…' : 'Selecionar serviço…'}
                                                                noOptionsMessage={({ inputValue }) =>
                                                                    inputValue ? 'Nenhum serviço corresponde' : 'Nenhum serviço disponível'
                                                                }
                                                                menuPortalTarget={document.body}
                                                                styles={{
                                                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                                }}
                                                            />
                                                        </div>

                                                        <CurrencyInput
                                                            name={`valor-${id}`}
                                                            value={(chosenValor[id] ?? Number.NaN)}
                                                            onValueChange={(v) => {
                                                                setChosenValor(prev => ({
                                                                    ...prev,
                                                                    [id]: Number.isNaN(v) ? null : v
                                                                }));
                                                            }}
                                                            placeholder={(() => {
                                                                const sid = chosenServico[id] ?? null;
                                                                const def = sid != null
                                                                    ? (servicosMap[id]?.find(o => o.servicoId === sid)?.value ?? null)
                                                                    : null;
                                                                return def != null ? `Valor padrão: R$ ${def.toFixed(2)}` : 'Valor do serviço (opcional)';
                                                            })()}
                                                        />

                                                        <Button
                                                            type="button"
                                                            color="danger"
                                                            className="btn btn-sm"
                                                            onClick={() => openRemoveModal(id)}
                                                            title="Remover/realocar fotógrafo"
                                                        >
                                                            Remover
                                                        </Button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <span className="text-muted">Nenhum fotógrafo selecionado</span>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-between mt-4">
                            <Button color="danger" onClick={handleDelete}>Excluir</Button>
                            <div>
                                <Button style={{ background: '#585858', color: '#fff' }} type="submit">Salvar</Button>
                                <Button color="secondary" className="ml-2" onClick={toggle}>Cancelar</Button>
                            </div>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            <Modal isOpen={removeModalOpen} toggle={() => setRemoveModalOpen(false)} backdrop="static">
                <ModalHeader toggle={() => setRemoveModalOpen(false)}>Remover fotógrafo</ModalHeader>
                <ModalBody>
                    <p className="form-control-label mb-2">O que deseja fazer com este fotógrafo?</p>

                    <FormGroup check>
                        <Label check>
                            <Input
                                type="radio"
                                name="removeAction"
                                checked={removeAction === 'remove'}
                                onChange={() => setRemoveAction('remove')}
                                className="form-control-label"
                            />
                            {' '}Remover da agenda atual
                        </Label>
                    </FormGroup>

                    <FormGroup check className="mt-2">
                        <Label check>
                            <Input
                                type="radio"
                                name="removeAction"
                                checked={removeAction === 'reallocate'}
                                onChange={() => setRemoveAction('reallocate')}
                                className="form-control-label"
                            />
                            {' '}Realocar para outro evento
                        </Label>
                    </FormGroup>

                    {removeAction === 'reallocate' && (
                        <div className="mt-3">
                            <Label className="form-control-label">Selecionar evento de destino</Label>
                            <Select
                                options={reallocateAgendaOptions}
                                value={reallocateAgendaOptions.find(opt => opt.value === reallocateAgendaId) || null}
                                onChange={(opt: any) => setReallocateAgendaId(opt?.value ?? null)}
                                placeholder="Selecione um evento..."
                                classNamePrefix="select"
                                menuPortalTarget={document.body}
                                styles={{
                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                            />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setRemoveModalOpen(false)}>Cancelar</Button>
                    <Button style={{ background: '#585858', color: '#fff' }} onClick={confirmRemoveOrReallocate}>Confirmar</Button>
                </ModalFooter>
            </Modal>

            <ModalConfirm
                openModal={confirmDeleteEventoOpen}
                closeModal={() => setConfirmDeleteEventoOpen(false)}
                handleConfirm={executeDeleteEvento}
                title="Excluir evento"
                message="Deseja excluir este evento? Esta ação não pode ser desfeita."
            />
        </>
    );
};

export default EditAgendaGerenteModal;